import React, {
    useState,
    useEffect,
    useCallback,
    forwardRef,
    useRef
} from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import moment from 'moment';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { GeneralButton } from 'sharedComponents/GeneralButton';
import { IconDatePicker } from 'svgIcons/MotionPortalIcons'; // default icon
import { formatDate } from 'helpers/dateTimeFormatter';
import 'react-day-picker/dist/style.css';
import './_singleDateSelector.scss';

// Helpers
const toDateObj = (v) => {
    if (!v) { return null; }
    if (v instanceof Date) { return v; }
    return moment(v).isValid() ? moment(v).toDate() : null;
};

const SingleDateSelectorComponent = ({
    // Controlled value
    value,
    // Callback returning (isoString, Date)
    onSelect,
    // Visibility control (external popover toggling)
    visibilityControl,
    // Behaviour
    confirmOnSelect = true,
    numberOfMonths = 1,
    minDate,
    maxDate,
    disabledDates = [],
    excludeToday = false,
    disablePast = false,
    disableFuture = false,
    // UI
    label,
    applyButtonText,
    cancelButtonText,
    placeholder,
    disabled = false,
    className,
    // Icons
    showLeftIcon = true,
    showRightIcon = false,
    leftIcon,
    rightIcon,
    // Placement (CSS hook)
    placement = 'auto',
    usePortal = false,
}, ref) => {
    const { t } = useTranslation();
    const internalRef = useRef(null);
    const popoverRef = ref || internalRef;
    const triggerRef = useRef(null);

    // Internal state mirrors value while open
    const initial = toDateObj(value);
    const [selected, setSelected] = useState(initial);
    const [month, setMonth] = useState(initial || moment().toDate());
    const [dirty, setDirty] = useState(false);
    const [computedPlacement, setComputedPlacement] = useState(placement);
    const [portalPos, setPortalPos] = useState({ top: 0, left: 0 });
    // Sync when closed or external value changes
    useEffect(() => {
        if (!visibilityControl?.visible) {
            const ext = toDateObj(value);
            setSelected(ext);
            setMonth(ext || moment().toDate());
            setDirty(false);
        }
    }, [visibilityControl?.visible, value]);

    const handleInternalSelect = useCallback((day) => {
        if (!day) { return; }
        setSelected(day);
        setDirty(true);
        if (!confirmOnSelect) {
            onSelect?.(day);
            visibilityControl?.setVisibilityOff();
        }
    }, [confirmOnSelect, onSelect, visibilityControl]);

    const apply = () => {
        if (selected) {
            onSelect?.(selected);
        }
        visibilityControl?.setVisibilityOff();
    };
    const cancel = () => visibilityControl?.setVisibilityOff();

    // Outside click closes
    useEffect(() => {
        if (!visibilityControl?.visible) { return; }
        const handler = (e) => {
            if (!popoverRef.current) { return; }
            if (!popoverRef.current.contains(e.target) && !triggerRef.current?.contains(e.target)) {
                visibilityControl?.setVisibilityOff();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [visibilityControl?.visible, visibilityControl, popoverRef]);

    // Keyboard
    useEffect(() => {
        if (!visibilityControl?.visible) { return; }
        const handler = (e) => {
            if (e.key === 'Escape') {
                visibilityControl?.setVisibilityOff();
            }
            if (e.key === 'Enter' && confirmOnSelect && selected && dirty) {
                apply();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [visibilityControl?.visible, confirmOnSelect, selected, dirty, apply]);

    // Disabled date rules
    const today = moment().startOf('day').toDate();
    const disabledRules = [...disabledDates];

    if (excludeToday) {
        disabledRules.push({ from: today, to: today });
    }
    if (disablePast) {
        disabledRules.push({ before: today });
    }
    if (disableFuture) {
        disabledRules.push({ after: today });
    }
    if (minDate) {
        const md = moment(toDateObj(minDate)).startOf('day').toDate();
        md && disabledRules.push({ before: md });
    }
    if (maxDate) {
        const mx = moment(toDateObj(maxDate)).startOf('day').toDate();
        mx && disabledRules.push({ after: mx });
    }

    const formatted = selected
        ? formatDate(selected)
        : placeholder || '';

    const calculatePlacement = useCallback(() => {
        if (placement !== 'auto' || !triggerRef.current) {
            return;
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate available space
        const spaceRight = viewportWidth - triggerRect.right;
        const spaceLeft = triggerRect.left;
        const spaceBottom = viewportHeight - triggerRect.bottom;
        const spaceTop = triggerRect.top;

        const popoverWidth = 350; // estimated width
        const popoverHeight = 400; // estimated height

        // Determine horizontal placement
        let horizontalPlacement;
        if (spaceRight >= popoverWidth) {
            horizontalPlacement = 'left';
        } else if (spaceLeft >= popoverWidth) {
            horizontalPlacement = 'right';
        } else {
            // Not enough space on either side, choose side with more space
            horizontalPlacement = spaceRight > spaceLeft ? 'left' : 'right';
        }

        // Determine vertical placement
        let verticalPlacement;
        if (spaceBottom >= popoverHeight) {
            verticalPlacement = 'bottom';
        } else if (spaceTop >= popoverHeight) {
            verticalPlacement = 'top';
        } else {
            verticalPlacement = spaceBottom > spaceTop ? 'bottom' : 'top';
        }

        // Combine horizontal and vertical placement
        setComputedPlacement(`${horizontalPlacement}-${verticalPlacement}`);
    }, [placement]);

    // Handle popover open with placement calculation
    const handleOpen = useCallback(() => {
        if (disabled) { return; }

        // Calculate placement first
        calculatePlacement();

        //  trigger fixed position from trigger rect so portal escapes overflow containers
        if (usePortal && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const opensAbove = window.innerHeight - rect.bottom < 420;
            const left = Math.max(8, rect.right - 320);
            setPortalPos(opensAbove
                ? { top: undefined, bottom: window.innerHeight - rect.top, left }
                : { top: rect.bottom, bottom: undefined, left }
            );
        }

        // Then open the popover
        visibilityControl?.setVisibilityOn();
    }, [disabled, calculatePlacement, visibilityControl, usePortal]);

    // Recalculate on window resize (only when visible)
    useEffect(() => {
        if (!visibilityControl?.visible || placement !== 'auto') {
            return;
        }

        window.addEventListener('resize', calculatePlacement);
        return () => window.removeEventListener('resize', calculatePlacement);
    }, [placement, visibilityControl?.visible, calculatePlacement]);

    // Fine-tune placement after popover renders
    useEffect(() => {
        if (!visibilityControl?.visible || placement !== 'auto' || !popoverRef.current) {
            return;
        }

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            calculatePlacement();
        });
    }, [visibilityControl?.visible, placement, calculatePlacement]);

    const finalPlacement = placement === 'auto' ? computedPlacement : placement;

    return (
        <div className={clsx('sdp-root', className, `sdp-placement-${finalPlacement}`)} role='singleDatePicker'>
            {label && <div className='sdp-label'>{label}</div>}
            <button
                ref={triggerRef}
                type='button'
                className={clsx('sdp-trigger', disabled && 'disabled')}
                onClick={handleOpen}
                aria-haspopup='dialog'
                aria-expanded={visibilityControl?.visible || false}
                aria-label={label || 'Select date'}
            >
                {showLeftIcon && <span className='sdp-icon-left' aria-hidden='true'>
                    {leftIcon || <IconDatePicker className='calendar-icon' />}
                </span>}
                <span className='sdp-trigger-text'>{formatted}</span>
                {showRightIcon && <span className='sdp-icon-right' aria-hidden='true'>
                    {rightIcon || <IconDatePicker className='calendar-icon' />}
                </span>}
            </button>

            {visibilityControl?.visible && (() => {
                const portalStyle = usePortal ? { top: portalPos.top, bottom: portalPos.bottom, left: portalPos.left } : {};
                const popoverEl =
                    <div
                        ref={popoverRef}
                        className='sdp-popover'
                        style={portalStyle}
                        role='dialog'
                        aria-modal='true'
                        aria-label={label ? `${label} date picker` : 'Date picker'}
                    >
                        <DayPicker
                            mode='single'
                            numberOfMonths={numberOfMonths}
                            month={month}
                            selected={selected}
                            onSelect={handleInternalSelect}
                            onMonthChange={setMonth}
                            disabled={disabledRules}
                        />

                        <div className='sdp-footer'>
                            {confirmOnSelect && <div className='sdp-actions'>
                                <GeneralButton
                                    type='discreet'
                                    text={cancelButtonText || t('ABB.Powertrain.Frontend.cancelLabel')}
                                    onClick={cancel}
                                    className='sdp-btn-cancel'
                                />
                                <GeneralButton
                                    type='primary'
                                    disabled={!dirty || !selected}
                                    text={applyButtonText || t('ABB.Powertrain.Frontend.applyBtnLbl')}
                                    onClick={dirty && selected ? apply : undefined}
                                    className='sdp-btn-apply'
                                />
                            </div>}
                        </div>
                    </div>;
                return usePortal ? ReactDOM.createPortal(popoverEl, document.body) : popoverEl;
            })()}
        </div>
    );
};

export default forwardRef(SingleDateSelectorComponent);
